/* eslint-disable ember/classic-decorator-hooks */
import { set } from '@ember/object';
import Service, { type Registry as Services, service } from '@ember/service';

export default class BaseStorageService extends Service {
  @service declare errorTracking: Services['error-tracking'];

  inMemoryStorage: Record<string, string> = {};
  declare intendedStorage: unknown;
  isSupported = false;
  declare storage: Storage;

  init(...args: Parameters<Service['init']>): void {
    super.init(...args);

    if (!this.intendedStorage) {
      throw new Error('No storage set! - check the storage service');
    }
    const isSupported = this.checkSupport();
    set(this, 'isSupported', isSupported);
    if (isSupported) {
      set(this, 'storage', this.intendedStorage as typeof this.storage);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  checkSupport() {
    try {
      const key = '__test_support_key__';
      (this.intendedStorage as typeof this.storage).setItem(key, key);
      (this.intendedStorage as typeof this.storage).removeItem(key);
      return true;
    } catch (error) {
      this.errorTracking.addBreadcrumb({
        message: 'checking version failed',
        data: error as Record<string, unknown>,
      });
      return false;
    }
  }

  clear(): void {
    if (this.isSupported) {
      // Disabling linting on this line, since this is a false negative.

      this.storage.clear();
    } else {
      set(this, 'inMemoryStorage', {});
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getItem(key: string) {
    if (this.isSupported) {
      return this.storage.getItem(key);
    }
    return this.inMemoryStorage[key] || null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  key(n: number) {
    if (this.isSupported) {
      return this.storage.key(n);
    }
    return Object.keys(this.inMemoryStorage)[n] || null;
  }

  removeItem(key: string): void {
    if (this.isSupported) {
      this.storage.removeItem(key);
    } else {
      delete this.inMemoryStorage[key];
    }
  }

  setItem(key: string, value: string): void {
    if (this.isSupported) {
      this.storage.setItem(key, value);
    } else {
      this.inMemoryStorage[key] = value;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'base/storage': BaseStorageService;
  }
}
